import React, { useState, useRef, useEffect, useMemo } from "react";
import { X, Save, Trash2, Plus, Minus, Edit2 } from "lucide-react";
import { KeywordSelector } from "./KeywordSelector";
import { ItemEditor } from "./ItemEditor";
import { SkillModal } from "./SkillModal";
import { MODEL_KEYWORDS, shouldTriggerSkillModal, applyModelUpgrades, calculateUpgradeCost, calculateModelValue, generateId, canAddItem, calculateEffectiveStats } from "./helpers";
import { Model, Item, Warband, Skill, ModelTemplate, ModelUpgrade, ItemTemplate } from "./types";
import { useForceSaveOnUnmount } from "./hooks/useAutoSave";

interface ModelEditorProps {
  model: Model;
  items: Item[];
  setItems: (items: Item[]) => void;
  warband: Warband;
  setWarband: React.Dispatch<React.SetStateAction<Warband>>;
  onSave: (updatedModel: Model, closeModal?: boolean) => void;
  onClose: () => void;
  onDelete: () => void;
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
  updateItemTemplates: (templates: ItemTemplate[]) => void;
  models: Model[];
  embedded?: boolean;
  backgroundImageUrl?: string;
  /** When true, opens with stat/item editing UI expanded (e.g. full modal from drawer). */
  startInEditMode?: boolean;
  onRequestEditStats?: () => void;
}

export const ModelEditor = ({
  model,
  items,
  setItems,
  warband,
  setWarband,
  onSave,
  onClose,
  onDelete,
  modelTemplates,
  itemTemplates,
  updateItemTemplates,
  models,
  embedded = false,
  backgroundImageUrl,
  startInEditMode = false,
  onRequestEditStats,
}: ModelEditorProps) => {
  // Force save when modal closes
  useForceSaveOnUnmount();

  const [editedModel, setEditedModel] = useState({ ...model });
  const [newScar, setNewScar] = useState("");
  const [showItemEditor, setShowItemEditor] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(startInEditMode);
  const [newAbility, setNewAbility] = useState("");
  const [newAbilityDescription, setNewAbilityDescription] = useState("");
  const [newSkill, setNewSkill] = useState({
    name: "",
    table: "",
    description: "",
  });
  const [itemFilterCategory, setItemFilterCategory] = useState<string | undefined>(undefined);
  const [showBgPreview, setShowBgPreview] = useState(false);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");

  const backgroundOptions = useMemo(
    () => [
      "anointed-heavy-infantry",
      "artillery-witch",
      "avarice-anointed",
      "azeb",
      "brazen-bull",
      "castigator",
      "chorister",
      "combat-biologist",
      "combat-engineer",
      "communicant-anti-tank-hunter",
      "death-commando",
      "goetic-warlock",
      "hell-knight",
      "herald-of-plague",
      "heretic-naval-anointed",
      "heretic-priest",
      "heretic-trooper",
      "hounds-of-black-grail",
      "jabirean-alchemist",
      "janissary",
      "jazbusi",
      "lion-of-jabir",
      "lord-of-tumors",
      "martyr-penitent",
      "mechanized-heavy-infantry",
      "mike-franchina-ecclesiastic-prisoner-x",
      "na-cleric",
      "observer",
      "pilgrim",
      "plague-guard",
      "plague-knight",
      "praetor",
      "procession-of-the-sacred-affliction-pilgrim",
      "procession-of-the-sacred-affliction",
      "prussia-cleric",
      "scripture-guardian",
      "shocktrooper",
      "sniper-priest",
      "stigmatic-nun",
      "sultanate-assassin",
      "sultanate-sapper",
      "trench-ghost-banshee",
      "trench-ghost-heretic-priest",
      "trench-pilgrim",
      "war-wolf",
      "warrior-saint",
      "witch-hunter-general",
      "yeoman",
    ].map((key) => ({
      key,
      url: `/images/models/backgrounds/${key}.webp`,
      thumbUrl: `/images/models/backgrounds/Thumbnails/Thumbnail ${key}.webp`,
    })),
    []
  );

  const openImagePopup = () => {
    setImageUrlInput(editedModel.customBgUrl || "");
    setShowImagePopup(true);
  };

  const saveImagePopup = () => {
    const cleanedUrl = imageUrlInput.trim();
    const updatedModel = {
      ...editedModel,
      customBgUrl: cleanedUrl ? cleanedUrl : null,
    };
    setEditedModel(updatedModel);
    onSave(updatedModel);
    setShowImagePopup(false);
  };

  const selectBackgroundOption = (url: string) => {
    setImageUrlInput(url);
  };

  // Track previous XP to detect external changes
  const prevXpRef = useRef(model.xp);

  // Sync editedModel with model prop changes
  React.useEffect(() => {
    setEditedModel({ ...model });
  }, [model]);

  // Check for XP threshold crossings when model.xp changes externally
  useEffect(() => {
    const currentXp = model.xp;
    const previousXp = prevXpRef.current;

    if (currentXp > previousXp && model.type === "elite") {
      const shouldGainSkill = shouldTriggerSkillModal(currentXp, previousXp, model.skills.length);
      if (shouldGainSkill) {
        setShowSkillModal(true);
      }
    }

    prevXpRef.current = currentXp;
  }, [model.xp, model.type, model.skills.length]);

  // Find the model template
  const modelTemplate = modelTemplates.find(t => t.id === model.templateId || t.id === model.id || t.name === model.name);

  // Calculate effective stats including all modifiers
  const effectiveStats = calculateEffectiveStats(editedModel, items, modelTemplate);

  // Handle upgrade toggling
  const handleUpgradeToggle = (upgradeId: string, checked: boolean) => {
    const currentUpgrades = editedModel.enabledUpgrades || [];
    const template = modelTemplate;
    if (!template?.upgrades) return;

    const upgrade = template.upgrades.find(u => u.id === upgradeId);
    if (!upgrade) return;

    let newUpgrades: string[];

    if (checked) {
      // Enable upgrade - check upgrade limits
      if (upgrade.limit) {
        // Count how many models currently have this upgrade active
        const modelsWithUpgrade = models.filter(model =>
          (model.enabledUpgrades || []).includes(upgradeId)
        ).length;

        if (modelsWithUpgrade >= upgrade.limit) {
          alert(`Upgrade limit reached! Only ${upgrade.limit} model(s) can have "${upgrade.name}" active at once.`);
          return;
        }
      }

      // Check if warband has enough ducats
      const upgradeCost = upgrade.cost || 0;
      if (upgradeCost > 0 && warband.ducats < upgradeCost) {
        alert(`Not enough ducats! This upgrade costs ${upgradeCost} ducats but you only have ${warband.ducats}.`);
        return;
      }

      // Add upgrade and deduct cost
      newUpgrades = [...currentUpgrades, upgradeId];
      if (upgradeCost > 0) {
        setWarband({ ...warband, ducats: warband.ducats - upgradeCost });
      }
    } else {
      // Disable upgrade - refund cost
      newUpgrades = currentUpgrades.filter(id => id !== upgradeId);
      const upgradeCost = upgrade.cost || 0;
      if (upgradeCost > 0) {
        setWarband({ ...warband, ducats: warband.ducats + upgradeCost });
      }
    }

    // Apply or remove upgrade effects
    let updatedModel = { ...editedModel, enabledUpgrades: newUpgrades };

    if (checked) {
      // Apply upgrade effects
      if (upgrade.effects.addKeywords) {
        const existingKeywords = new Set(updatedModel.keywords);
        upgrade.effects.addKeywords.forEach((keyword: string) => existingKeywords.add(keyword));
        updatedModel.keywords = Array.from(existingKeywords);
      }

      if (upgrade.effects.addAbilities) {
        const existingAbilities = Array.isArray(updatedModel.abilities) ? [...updatedModel.abilities] : [];
        const normalizedAbilities: Array<{ name: string; description: string }> = existingAbilities.map((ability: any) => {
          if (typeof ability === 'string') {
            return { name: ability, description: '' };
          }
          return ability;
        });

        upgrade.effects.addAbilities.forEach((ability: { name: string; description: string }) => {
          const existingIndex = normalizedAbilities.findIndex((a) => a.name === ability.name);
          if (existingIndex === -1) {
            normalizedAbilities.push(ability);
          } else {
            normalizedAbilities[existingIndex] = ability;
          }
        });

        updatedModel.abilities = normalizedAbilities;
      }

      if (upgrade.effects.addItems && itemTemplates) {
        const newItems = [...updatedModel.items];
        upgrade.effects.addItems.forEach((itemTemplateId: string) => {
          const itemTemplate = itemTemplates.find(it => it.id === itemTemplateId || it.templateName === itemTemplateId || it.name === itemTemplateId);
          if (itemTemplate) {
            // Check if this item type is already on the model to avoid duplicates
            const existingItemIndex = items.findIndex(item =>
              item.name === itemTemplate.name &&
              updatedModel.items.includes(item.id) &&
              !item.isInbuilt // Don't count inbuilt items as duplicates for template items
            );

            if (existingItemIndex === -1) {
              // Create new item instance
              const newItem = {
                id: generateId(),
                name: itemTemplate.name,
                category: itemTemplate.category,
                keywords: [...itemTemplate.keywords],
                range: itemTemplate.range,
                armourModifier: itemTemplate.armourModifier,
                notes: itemTemplate.notes,
                cost: itemTemplate.cost,
                costCurrency: itemTemplate.costCurrency,
                isInbuilt: false,
              };
              newItems.push(newItem.id);
              setItems([...items, newItem]);
            }
          }
        });
        updatedModel.items = newItems;
      }

      if (upgrade.effects.addInbuiltEquipment) {
        const newItems = [...updatedModel.items];
        upgrade.effects.addInbuiltEquipment.forEach((equipment: any) => {
          const newItem = {
            id: generateId(),
            name: equipment.name,
            category: equipment.category,
            keywords: [...equipment.keywords],
            range: equipment.range,
            armourModifier: equipment.armourModifier,
            notes: equipment.notes,
            cost: 0,
            costCurrency: "free" as const,
            isInbuilt: true,
          };
          newItems.push(newItem.id);
          setItems([...items, newItem]);
        });
        updatedModel.items = newItems;
      }
    } else {
      // Remove upgrade effects
      if (upgrade.effects.addKeywords) {
        const keywordsToRemove = new Set(upgrade.effects.addKeywords);
        updatedModel.keywords = updatedModel.keywords.filter(k => !keywordsToRemove.has(k));
      }

      if (upgrade.effects.addAbilities) {
        const abilitiesToRemove = new Set(upgrade.effects.addAbilities.map(a => a.name));
        if (Array.isArray(updatedModel.abilities)) {
          updatedModel.abilities = (updatedModel.abilities as any[]).filter((ability: any) => {
            const abilityName = typeof ability === 'string' ? ability : ability.name;
            return !abilitiesToRemove.has(abilityName);
          });
        }
      }

      if (upgrade.effects.addItems && itemTemplates) {
        const itemNamesToRemove = new Set();
        upgrade.effects.addItems.forEach((itemTemplateId: string) => {
          const itemTemplate = itemTemplates.find(it => it.id === itemTemplateId || it.templateName === itemTemplateId || it.name === itemTemplateId);
          if (itemTemplate) {
            itemNamesToRemove.add(itemTemplate.name);
          }
        });

        // Remove one instance of each item type that was added by this upgrade
        const itemsToKeep = [];
        const removedNames = new Set();

        for (const itemId of updatedModel.items) {
          const item = items.find(i => i.id === itemId);
          if (item && itemNamesToRemove.has(item.name) && !item.isInbuilt && !removedNames.has(item.name)) {
            // Skip this item (remove one instance)
            removedNames.add(item.name);
          } else {
            // Keep this item
            itemsToKeep.push(itemId);
          }
        }

        updatedModel.items = itemsToKeep;
      }

      if (upgrade.effects.addInbuiltEquipment) {
        const equipmentNamesToRemove = new Set(upgrade.effects.addInbuiltEquipment.map((e: any) => e.name));

        // Remove inbuilt equipment added by this upgrade
        const itemsToKeep = updatedModel.items.filter(itemId => {
          const item = items.find(i => i.id === itemId);
          return item && !(item.isInbuilt && equipmentNamesToRemove.has(item.name));
        });
        updatedModel.items = itemsToKeep;
      }
    }

    setEditedModel(updatedModel);

    // Save the model immediately after upgrade change
    onSave(updatedModel, false);
  };


  const handleSave = () => {
    // Check if XP increase triggers skill selection
    const xpIncreased = editedModel.xp > model.xp;
    const shouldGainSkill = xpIncreased && editedModel.type === "elite" &&
      shouldTriggerSkillModal(editedModel.xp, model.xp, editedModel.skills.length);

    if (shouldGainSkill) {
      setShowSkillModal(true);
    } else {
      onSave(editedModel, true); // Close modal after explicit save
      if (isEditMode) {
        setIsEditMode(false);
      } else {
    onClose();
      }
    }
  };

  const handleSkillSelected = (newSkill: Skill) => {
    const updatedModel = {
      ...editedModel,
      skills: [...editedModel.skills, newSkill],
    };
    setEditedModel(updatedModel);
    setShowSkillModal(false);
    onSave(updatedModel, !isEditMode); // Close modal only if not in edit mode
  };

  const handleXpIncrement = () => {
    const newXp = editedModel.xp + 1;
    const updatedModel = { ...editedModel, xp: newXp };

    // Save the model immediately
    onSave(updatedModel);
    setEditedModel(updatedModel);

    // Check if this XP increase triggers skill selection
    const shouldGainSkill = updatedModel.type === "elite" &&
      shouldTriggerSkillModal(newXp, editedModel.xp, updatedModel.skills.length);

    if (shouldGainSkill) {
      setShowSkillModal(true);
    }
    // Modal stays open - user can continue viewing/editing
  };

  const handleXpDecrement = () => {
    const newXp = Math.max(0, editedModel.xp - 1);
    const updatedModel = { ...editedModel, xp: newXp };
    setEditedModel(updatedModel);
    onSave(updatedModel);
  };

  const handleModelNameBlur = () => {
    const trimmed = editedModel.name.trim();
    const resolvedName = trimmed || model.name;
    const next = { ...editedModel, name: resolvedName };
    setEditedModel(next);
    if (resolvedName !== model.name) {
      onSave(next, false);
    }
  };

  const addAbility = () => {
    if (newAbility.trim()) {
      const updatedModel = {
        ...editedModel,
        abilities: [
          ...editedModel.abilities,
          {
            name: newAbility.trim(),
            description: newAbilityDescription.trim(),
          },
        ] as string[] | { name: string; description: string }[],
      };
      setEditedModel(updatedModel);
      onSave(updatedModel); // Save immediately
      setNewAbility("");
      setNewAbilityDescription("");
    }
  };

  const addSkill = () => {
    if (newSkill.name.trim() && newSkill.table.trim()) {
      const updatedModel = {
        ...editedModel,
        skills: [...editedModel.skills, { ...newSkill }],
      };
      setEditedModel(updatedModel);
      onSave(updatedModel); // Save immediately
      setNewSkill({ name: "", table: "", description: "" });
    } else {
      alert("Please enter skill name and table");
    }
  };

  const addScar = () => {
    if (newScar.trim()) {
      const updatedModel = {
        ...editedModel,
        scars: [...editedModel.scars, newScar.trim()],
      };
      setEditedModel(updatedModel);
      onSave(updatedModel); // Save immediately
      setNewScar("");
    }
  };

  const removeScar = (index: number) => {
    const updatedModel = {
      ...editedModel,
      scars: editedModel.scars.filter((_, i) => i !== index),
    };
    setEditedModel(updatedModel);
    onSave(updatedModel); // Save immediately
  };

  const addItem = (newItem: Item) => {
    // Check item limits
    const itemTemplate = itemTemplates.find(t => t.name === newItem.name);
    if (itemTemplate) {
      const validation = canAddItem(itemTemplate, warband, items, models, itemTemplates);
      if (!validation.allowed) {
        alert(validation.reason);
        return;
      }
    }

    // Check if item has a cost
    const itemCost = newItem.cost || 0;
    const itemCostCurrency = newItem.costCurrency || "ducats";

    // Deduct ducats for items that cost ducats
    if (itemCost > 0 && itemCostCurrency === "ducats") {
      if (warband.ducats < itemCost) {
        alert(`Not enough ducats! This item costs ${itemCost} ducats but you only have ${warband.ducats}.`);
        return;
      }

      // Deduct ducats from warband
      setWarband({ ...warband, ducats: warband.ducats - itemCost });
    }

    // Deduct glory for items that cost glory
    if (itemCost > 0 && itemCostCurrency === "glory") {
      if (warband.glory < itemCost) {
        alert(`Not enough glory! This item costs ${itemCost} glory but you only have ${warband.glory}.`);
        return;
      }

      // Deduct glory from warband
      setWarband({ ...warband, glory: warband.glory - itemCost });
    }

    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    const updatedModel = {
      ...editedModel,
      items: [...editedModel.items, newItem.id],
    };
    setEditedModel(updatedModel);
    onSave(updatedModel); // Save immediately
    setShowItemEditor(false);
    setItemFilterCategory(undefined);
  };

  const removeItem = (itemId: string) => {
    const updatedModel = {
      ...editedModel,
      items: editedModel.items.filter((id) => id !== itemId),
    };
    setEditedModel(updatedModel);
    onSave(updatedModel); // Save immediately
  };

  const moveItemToWarchest = (itemId: string) => {
    // First, add to warchest
    setWarband({
      ...warband,
      warchest: [...warband.warchest, itemId]
    });

    // Then remove from model (identical to removeItem)
    const updatedModel = {
      ...editedModel,
      items: editedModel.items.filter((id) => id !== itemId),
    };
    setEditedModel(updatedModel);
    onSave(updatedModel); // Save immediately
  };

  const handleSellItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    // Calculate 50% refund
    const itemCost = item.cost || 0;
    const itemCostCurrency = item.costCurrency || "ducats";
    const refundAmount = Math.floor(itemCost / 2);

    // Add refund to warband
    if (refundAmount > 0) {
      if (itemCostCurrency === "ducats") {
        setWarband((prev: Warband) => ({ ...prev, ducats: prev.ducats + refundAmount }));
      } else if (itemCostCurrency === "glory") {
        setWarband((prev: Warband) => ({ ...prev, glory: prev.glory + refundAmount }));
      }
    }

    // Remove item from model
    const updatedModel = {
      ...editedModel,
      items: editedModel.items.filter((id) => id !== itemId),
    };
    setEditedModel(updatedModel);
    onSave(updatedModel);
  };

  const handleRefundItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    // Calculate 100% refund
    const itemCost = item.cost || 0;
    const itemCostCurrency = item.costCurrency || "ducats";

    // Add refund to warband
    if (itemCost > 0) {
      if (itemCostCurrency === "ducats") {
        setWarband((prev: Warband) => ({ ...prev, ducats: prev.ducats + itemCost }));
      } else if (itemCostCurrency === "glory") {
        setWarband((prev: Warband) => ({ ...prev, glory: prev.glory + itemCost }));
      }
    }

    // Remove item from model
    const updatedModel = {
      ...editedModel,
      items: editedModel.items.filter((id) => id !== itemId),
    };
    setEditedModel(updatedModel);
    onSave(updatedModel);
  };

  const handleRefundModel = () => {
    // Use total value (base + equipment + upgrades) for 100% refund
    const modelValue = calculateModelValue(editedModel, items, modelTemplate);

    if (modelValue.totalDucats > 0 || modelValue.totalGlory > 0) {
      setWarband((prev: Warband) => ({
        ...prev,
        ducats: prev.ducats + modelValue.totalDucats,
        glory: prev.glory + modelValue.totalGlory
      }));
    }

    // Delete the model entirely
    onDelete();
  };

  const equippedItems = items.filter((item) =>
    editedModel.items.includes(item.id)
  );

  const contentClassName = embedded
    ? backgroundImageUrl
      ? "bg-[#1a1a1a] border-l border-gray-700 h-full w-full overflow-y-auto text-sm"
      : "bg-gray-950/10 border-l border-gray-700 h-full w-full overflow-y-auto text-sm"
    : "bg-[#1a1a1a] w-full max-w-6xl max-h-[94vh] overflow-y-auto text-sm";

  const contentStyle = embedded && backgroundImageUrl ? {
    backgroundImage: `url(${backgroundImageUrl})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  } : undefined;

  return (
    <div
      className={
        embedded
          ? "h-full w-full"
          : "fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3"
      }
      style={
        embedded
          ? undefined
          : ({
              backgroundImage: `url(${backgroundImageUrl || "/images/zhi-2.jpg"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundBlendMode: "overlay",
            } as React.CSSProperties)
      }
    >
      <div
        className={contentClassName}
        style={contentStyle}
      >
        {/* Header */}
        {!embedded && (
        <div className="flex justify-between items-start p-3 border-b border-black/50">
          <div className="flex-1">
            {isEditMode ? (
              <h2 className="text-base font-semibold text-ledger-ink uppercase tracking-wider">Edit Model</h2>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="text"
                    aria-label="Model name"
                    value={editedModel.name}
                    onChange={(e) => setEditedModel({ ...editedModel, name: e.target.value })}
                    onBlur={handleModelNameBlur}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    className={`flex-1 min-w-[10rem] max-w-full sm:max-w-2xl bg-transparent rounded px-1.5 py-1 -mx-1.5 -my-1 hover:bg-black/15 focus:bg-black/25 focus:outline-none focus:ring-0 border-0 cursor-text text-2xl sm:text-3xl font-bold uppercase tracking-wider selection:bg-red-900/40 ${embedded ? 'text-yellow-500' : 'text-ledger-ink'}`}
                  />
                  <button
                    onClick={handleXpIncrement}
                    className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-1 border border-[#6b2a2a] text-xs font-semibold flex items-center gap-1"
                  >
                    <Plus size={12} />
                    1 XP
                  </button>
                  <button
                    onClick={handleXpDecrement}
                    className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-1 border border-[#6b2a2a] text-xs font-semibold flex items-center gap-1"
                  >
                    <Minus size={12} />
                    1 XP
                  </button>
                </div>
                {!embedded && (
                <div className="flex items-center gap-4">
                  <div className="text-sm text-ledger-muted uppercase tracking-wide">
                    Warband Ducats:
                  </div>
                  <div className="text-sm font-semibold text-ledger-ink">
                    {warband.ducats} 👑
                  </div>
                </div>
                )}
                {!embedded && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-ledger-muted font-semibold uppercase tracking-wider">{editedModel.class}</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold border ${
                      editedModel.type === "elite"
                        ? "bg-black/30 border-[#6b2a2a] text-ledger-gold"
                        : "bg-black/20 border-[#3a2a22] text-ledger-ink"
                    }`}>
                      {editedModel.type?.toUpperCase()}
                    </span>
                  </div>
                  {(() => {
                    const template = modelTemplates.find(t => t.id === editedModel.id || t.name === editedModel.name);
                    const modelValue = calculateModelValue(editedModel, items, template);
                    return (modelValue.totalDucats > 0 || modelValue.totalGlory > 0) ? (
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-400 uppercase tracking-wide">
                          Model Value:
                        </div>
                        <div className="text-sm font-bold text-gray-300">
                          {modelValue.totalDucats > 0 && `${modelValue.totalDucats}👑`}
                          {modelValue.totalDucats > 0 && modelValue.totalGlory > 0 && " + "}
                          {modelValue.totalGlory > 0 && `${modelValue.totalGlory}☼`}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
                )}
              </div>
            )}
          </div>

          {!embedded && (
          <div className="flex gap-2 ml-4">
            {!isEditMode && (
              <button
                onClick={() => setIsEditMode(true)}
                className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-1 border border-[#6b2a2a] uppercase tracking-wider text-xs font-semibold"
              >
                EDIT STATS
              </button>
            )}
            <button
              onClick={() => {
                onSave(editedModel, false);
                onClose();
              }}
              className="text-gray-400 hover:text-gray-300 p-1 rounded hover:bg-gray-800/50"
            >
              <X size={20} />
          </button>
          </div>
          )}
        </div>
        )}

        <div className="p-3 space-y-3">
          {isEditMode ? (
            /* EDIT MODE - Full editing capabilities */
            <div className="space-y-4">
              {/* Basic Info Editing */}
              <div className="bg-gray-900/90 rounded border border-gray-700 p-4 shadow-lg">
                <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide mb-3 border-b border-gray-700 pb-1">Basic Information</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Name</label>
              <input
                type="text"
                value={editedModel.name}
                      onChange={(e) => setEditedModel({ ...editedModel, name: e.target.value })}
                      className="w-full bg-gray-900/90 text-gray-300 px-3 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none text-sm"
              />
            </div>
            <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Class</label>
              <input
                type="text"
                value={editedModel.class}
                      onChange={(e) => setEditedModel({ ...editedModel, class: e.target.value })}
                      className="w-full bg-gray-900/90 text-gray-300 px-4 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
              />
            </div>
            <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Type</label>
                    <select
                      value={editedModel.type || "trooper"}
                      onChange={(e) => setEditedModel({ ...editedModel, type: e.target.value as "elite" | "trooper" })}
                      className="w-full bg-gray-900/90 text-gray-300 px-4 py-2 rounded border border-gray-700 focus:border-red-800 focus:outline-none"
                    >
                      <option value="trooper">Trooper</option>
                      <option value="elite">Elite</option>
                    </select>
              </div>
            </div>
          </div>

                <div className="mt-4 border-t border-gray-700 pt-4">
                  <div className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-2">Visuals</div>
                  <label className="block text-xs text-gray-300 mb-1">
                    Custom Sidebar Background Image URL
                  </label>
                  <input
                    type="text"
                    value={editedModel.customBgUrl || ""}
                    onChange={(e) =>
                      setEditedModel({
                        ...editedModel,
                        customBgUrl: e.target.value.trim() ? e.target.value.trim() : null,
                      })
                    }
                    placeholder="https://..."
                    className="w-full bg-black/40 text-gray-100 px-3 py-2 border border-gray-700 focus:outline-none text-sm"
                  />
                  <div className="mt-1 text-xs text-gray-400">
                    Paste direct image link (e.g. Imgur/Discord). Used as faded background when this model is viewed.
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowBgPreview((v) => !v)}
                      className="bg-[#222] hover:bg-[#2a2a2a] text-[#E6E5D3] px-3 py-2 text-xs uppercase tracking-wider font-semibold"
                      disabled={!editedModel.customBgUrl}
                    >
                      Preview
                    </button>
                    {editedModel.customBgUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditedModel({ ...editedModel, customBgUrl: null });
                          setShowBgPreview(false);
                        }}
                        className="bg-[#6b1c1c] hover:bg-[#5c1414] text-[#E6E5D3] px-3 py-2 text-xs uppercase tracking-wider font-semibold"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {showBgPreview && editedModel.customBgUrl && (
                    <div className="mt-3">
                      <img
                        src={editedModel.customBgUrl}
                        alt="Background preview"
                        className="h-[100px] w-[100px] object-cover"
                        onError={() => setShowBgPreview(false)}
                      />
                    </div>
                  )}
                </div>

              {/* Stats Editing */}
              <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3 border-b border-black/50 pb-1">COMBAT STATISTICS</h3>
          <div className="grid grid-cols-5 gap-4">
                  {[
                    { key: 'movement', label: 'Movement', suffix: '"' },
                    { key: 'melee', label: 'Melee', suffix: 'D' },
                    { key: 'ranged', label: 'Ranged', suffix: 'D' },
                    { key: 'baseArmour', label: 'Base Armour', suffix: '' }
                  ].map(({ key, label, suffix }) => (
                    <div key={key}>
                      <label className="block text-sm font-semibold text-white mb-2">{label}</label>
              <input
                type="number"
                        value={editedModel.stats[key as keyof typeof editedModel.stats]}
                        onChange={(e) => setEditedModel({
                    ...editedModel,
                    stats: {
                      ...editedModel.stats,
                            [key]: parseInt(e.target.value) || 0,
                    },
                        })}
                        className="w-full bg-black/40 text-white px-4 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none text-center font-bold"
              />
            </div>
                  ))}
                </div>
              </div>

            </div>
          ) : (
            /* VIEW MODE - Compact Character Sheet */
            <>
              {/* Stats Section */}
              <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                <h3 className="text-sm font-bold text-white uppercase tracking-wide mb-3 border-b border-black/50 pb-1">Combat Statistics</h3>
                <div className="grid grid-cols-5 gap-2">
                  <div className="text-center p-3 bg-black/40 rounded border border-black/50">
                    <div className="text-lg font-bold text-white mb-0.5">{effectiveStats.movement}"</div>
                    <div className="text-xs text-ledger-muted uppercase tracking-wider">MOVEMENT</div>
                    <div className="text-xs text-ledger-muted mt-0.5">inches</div>
                    {effectiveStats.movement !== editedModel.stats.movement && (
                      <div className="text-xs text-blue-400 mt-0.5">
                        ({editedModel.stats.movement !== undefined ? (editedModel.stats.movement > effectiveStats.movement ? '+' : '') + (effectiveStats.movement - editedModel.stats.movement) : ''})
                      </div>
                    )}
                  </div>
                  <div className="text-center p-3 bg-black/40 rounded border border-black/50">
                    <div className="text-lg font-bold text-white mb-0.5">{effectiveStats.melee >= 0 ? '+' : ''}{effectiveStats.melee}D</div>
                    <div className="text-xs text-ledger-muted uppercase tracking-wider">MELEE</div>
                    <div className="text-xs text-ledger-muted mt-0.5">dice</div>
                    {effectiveStats.melee !== editedModel.stats.melee && (
                      <div className="text-xs text-blue-400 mt-0.5">
                        ({editedModel.stats.melee !== undefined ? (editedModel.stats.melee > effectiveStats.melee ? '+' : '') + (effectiveStats.melee - editedModel.stats.melee) : ''})
                      </div>
                    )}
                  </div>
                  <div className="text-center p-3 bg-black/40 rounded border border-black/50">
                    <div className="text-lg font-bold text-white mb-0.5">{effectiveStats.ranged >= 0 ? '+' : ''}{effectiveStats.ranged}D</div>
                    <div className="text-xs text-ledger-muted uppercase tracking-wider">RANGED</div>
                    <div className="text-xs text-ledger-muted mt-0.5">dice</div>
                    {effectiveStats.ranged !== editedModel.stats.ranged && (
                      <div className="text-xs text-blue-400 mt-0.5">
                        ({editedModel.stats.ranged !== undefined ? (editedModel.stats.ranged > effectiveStats.ranged ? '+' : '') + (effectiveStats.ranged - editedModel.stats.ranged) : ''})
                      </div>
                    )}
                  </div>
                  <div className="text-center p-3 bg-black/40 rounded border border-black/50">
                    <div className="text-lg font-bold text-white mb-0.5">{effectiveStats.finalArmour}</div>
                    <div className="text-xs text-ledger-muted uppercase tracking-wider">ARMOUR</div>
                    <div className="text-xs text-ledger-muted mt-0.5">modifier</div>
                    {effectiveStats.finalArmour !== editedModel.stats.finalArmour && (
                      <div className="text-xs text-blue-400 mt-0.5">
                        ({editedModel.stats.finalArmour !== undefined ? (editedModel.stats.finalArmour > effectiveStats.finalArmour ? '+' : '') + (effectiveStats.finalArmour - editedModel.stats.finalArmour) : ''})
                      </div>
                    )}
                  </div>
                  <div className="text-center p-3 bg-black/40 rounded border border-black/50">
                    <div className="text-lg font-bold text-white mb-0.5">{editedModel.xp}</div>
                    <div className="text-xs text-ledger-muted uppercase tracking-wider">EXPERIENCE</div>
                    <div className="text-xs text-ledger-gold mt-0.5 font-medium">
                      {editedModel.type === "elite" ? (() => {
                        const thresholds = [2, 4, 7, 10, 13, 16, 19, 22, 25];
                        const nextThreshold = thresholds.find(t => t > editedModel.xp);
                        return nextThreshold ? `NEXT: ${nextThreshold}` : "MAX LEVEL";
                      })() : "TROOPER"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Upgrades Section */}
              {modelTemplate?.upgrades && modelTemplate.upgrades.length > 0 && (
                <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                  <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3 border-b border-black/50 pb-1">
                    UPGRADES & ENHANCEMENTS
                  </h3>
                  <div className="space-y-3">
                    {modelTemplate.upgrades.map((upgrade) => {
                      const isEnabled = (editedModel.enabledUpgrades || []).includes(upgrade.id);
                      const upgradeCost = upgrade.cost || 0;
                      const upgradeLimit = upgrade.limit;

                      return (
                        <div key={upgrade.id} className="flex items-center justify-between p-3 bg-black/40 rounded border border-black/50">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={(e) => handleUpgradeToggle(upgrade.id, e.target.checked)}
                                  className="w-4 h-4 text-red-900 bg-gray-800 border-gray-600 rounded focus:ring-red-900 focus:ring-2"
                                />
                                <span className="text-white font-medium">{upgrade.name}</span>
              </label>
                              <div className="flex gap-2">
                                {upgradeCost > 0 && (
                                  <span className={`text-sm font-bold px-2 py-1 rounded ${
                                    isEnabled ? 'bg-red-900/50 text-red-400' : 'bg-gray-700/50 text-gray-500'
                                  }`}>
                                    {upgradeCost} ducats
                                  </span>
                                )}
                                {upgradeLimit && (
                                  <span className="text-sm font-bold px-2 py-1 rounded bg-blue-900/50 text-blue-400">
                                    Limit: {upgradeLimit}
                                  </span>
                                )}
                              </div>
                            </div>
                            {upgrade.description && (
                              <div className="text-sm text-ledger-muted mt-1 ml-6">
                                {upgrade.description}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              {/* Equipment Section - Compact categories */}
              <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3 border-b border-black/50 pb-1">EQUIPMENT & WARGEAR</h3>

                <div className="space-y-3">
                  {(["melee", "ranged", "armour", "shield", "grenade", "equipment"] as const).map((category) => {
                    const categoryItems = equippedItems.filter(item => item.category === category);
                    return (
                      <div key={category} className="border border-black/50 rounded overflow-hidden">
                        <div className="bg-black/40 px-3 py-2 border-b border-black/50">
                          <div className="flex justify-between items-center">
                            <h4 className="text-sm font-bold text-white uppercase tracking-wide">
                              {category} ({categoryItems.length})
                            </h4>
                            <button
                              onClick={() => {
                                setItemFilterCategory(category);
                                setShowItemEditor(true);
                              }}
                              className="text-red-600 hover:text-red-500 text-sm px-1.5 py-0.5 rounded hover:bg-red-950/30"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="p-3">
                          {categoryItems.length === 0 ? (
                            <div className="text-ledger-muted text-xs italic text-center py-2">
                              NO {category.toUpperCase()} EQUIPPED
            </div>
                          ) : (
                            <div className="grid gap-2">
                              {categoryItems.map((item) => (
                                <div key={item.id} className="bg-black/30 rounded p-2 border border-black/50">
                                  <div className="flex justify-between items-start mb-1">
            <div>
                                      <div className="flex items-center gap-2">
                                        <h5 className={`font-bold text-sm ${item.costCurrency === 'glory' ? 'text-ledger-gold' : 'text-white'}`}>{item.name}</h5>
                                        {item.isInbuilt && (
                                          <span className="text-xs bg-yellow-900/50 text-yellow-400 px-1.5 py-0.5 rounded border border-yellow-700">
                                            BUILT-IN
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-ledger-muted uppercase tracking-wider">{item.category}</div>
                                    </div>
                                    <div className="flex gap-1">
                                      {item.isInbuilt ? (
                                        <div className="text-xs text-yellow-500 font-bold px-2 py-1 border border-yellow-600 rounded bg-yellow-900/20">
                                          BUILT-IN
                                        </div>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => moveItemToWarchest(item.id)}
                                            className="bg-blue-900/50 hover:bg-blue-800/70 text-blue-300 px-2 py-1 rounded text-xs font-bold border border-blue-700"
                                            title="Move to Warchest"
                                          >
                                            ↗
                                          </button>
                                          <button
                                            onClick={() => {
                                              console.log('SELL ITEM clicked for', item.name);
                                              handleSellItem(item.id);
                                            }}
                                            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs border border-gray-600 uppercase tracking-wider"
                                            title="Sell Item (50% refund)"
                                          >
                                            SELL
                                          </button>
                                          <button
                                            onClick={() => {
                                              console.log('REFUND ITEM clicked for', item.name);
                                              handleRefundItem(item.id);
                                            }}
                                            className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs border border-gray-600 uppercase tracking-wider"
                                            title="Refund Item (100%)"
                                          >
                                            REFUND
                                          </button>
                                          <button
                                            onClick={() => removeItem(item.id)}
                                            className="bg-red-900/50 hover:bg-red-800/70 text-red-300 px-2 py-1 rounded text-xs font-bold border border-red-700"
                                            title="Remove item"
                                          >
                                            ×
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {item.keywords.map((keyword, idx) => (
                                      <span key={idx} className="bg-red-950/40 text-red-600 px-1.5 py-0.5 rounded text-xs uppercase tracking-wider">
                                        {keyword}
                                      </span>
                                    ))}
                                  </div>

                                  {(item.range || item.armourModifier || item.cost) && (
                                    <div className="text-xs text-ledger-muted space-y-0.5">
                                      {item.range && <div>RANGE: {item.range}"</div>}
                                      {item.armourModifier && <div>ARMOUR: {item.armourModifier}</div>}
                                      {item.cost && item.costCurrency && (
                                        <div className={`font-medium ${item.costCurrency === 'glory' ? 'text-ledger-gold' : 'text-white'}`}>
                                          {item.cost} {item.costCurrency === "ducats" ? "👑" : "☼"}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {item.notes && (
                                    <div className="text-xs text-gray-600 mt-1 italic">
                                      {item.notes}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>

              {/* Abilities & Skills Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Abilities */}
                {editedModel.abilities.length > 0 && (
                  <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                    <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3 border-b border-black/50 pb-1">ABILITIES</h3>
                    <div className="space-y-2">
                      {editedModel.abilities.map((ability, idx) => (
                        <div key={idx} className="border-l-2 border-black/50 pl-3 bg-black/40 p-2 rounded">
                          <div className="font-semibold text-white text-sm mb-0.5">
                            {typeof ability === "string"
                              ? ability
                              : ability.name || "Unnamed Ability"}
                          </div>
                          {typeof ability !== "string" && ability.description && (
                            <div className="text-ledger-muted text-xs">
                              {ability.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skills (Elite Only) */}
                {editedModel.type === "elite" && (
                  <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                    <h3 className="text-lg font-bold text-ledger-gold uppercase tracking-wide mb-3 border-b border-black/50 pb-1">ELITE SKILLS</h3>
                    {editedModel.skills.length > 0 ? (
                      <div className="space-y-2">
                        {editedModel.skills.map((skill, idx) => (
                          <div key={idx} className="border-l-2 border-black/50 pl-3 bg-black/40 p-2 rounded">
                            <div className="font-semibold text-white text-sm mb-0.5">{skill.name}</div>
                            <div className="text-ledger-muted text-xs mb-0.5 uppercase tracking-wider">
                              {skill.table === "Stealth_and_Speed" ? "Stealth & Speed" : skill.table}
                            </div>
                            {skill.description && (
                              <div className="text-ledger-muted text-xs">
                                {skill.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-ledger-muted text-xs italic text-center py-4">
                        NO ELITE SKILLS LEARNED YET
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Battle Scars */}
              {editedModel.scars.length > 0 && (
                <div className="bg-black/60 rounded border border-black/50 p-4 shadow-lg">
                  <h3 className="text-lg font-bold text-white uppercase tracking-wide mb-3 border-b border-black/50 pb-1">BATTLE SCARS</h3>
                  <div className="grid gap-2">
                    {editedModel.scars.map((scar, idx) => (
                      <div key={idx} className="bg-black/40 rounded p-2 border border-black/50 flex justify-between items-center">
                        <span className="text-white text-sm">{scar}</span>
                        <button
                          onClick={() => removeScar(idx)}
                          className="text-red-600 hover:text-red-500 text-xs px-1.5 py-0.5 rounded hover:bg-red-950/30"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {isEditMode && (
            <>
          <div>
                <label className="block text-sm font-bold text-white mb-1 uppercase tracking-wide">
              Keywords
            </label>
            <KeywordSelector
              selected={editedModel.keywords}
              onChange={(kw) =>
                setEditedModel({ ...editedModel, keywords: kw })
              }
              availableKeywords={MODEL_KEYWORDS}
            />
          </div>

          <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-white uppercase tracking-wide">
              Abilities
            </label>
                </div>
            <div className="space-y-2">
              {editedModel.abilities.map((ability, idx) => (
                <div key={idx} className="bg-ledger-paper/60 px-3 py-2 rounded">
                      <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-ledger-ink">
                    {typeof ability === "string"
                      ? ability
                      : ability.name || "Unnamed Ability"}
                  </span>
                        <button
                          onClick={() =>
                            setEditedModel({
                              ...editedModel,
                              abilities: (editedModel.abilities as (string | { name: string; description: string })[]).filter((_: string | { name: string; description: string }, i: number) => i !== idx) as string[] | { name: string; description: string }[],
                            })
                          }
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <textarea
                        value={
                          typeof ability === "string"
                            ? ""
                            : ability.description || ""
                        }
                        onChange={(e) => {
                          const updatedAbilities = [...editedModel.abilities] as any[];
                          updatedAbilities[idx] = {
                            ...(updatedAbilities[idx] as any),
                            description: e.target.value,
                          };
                          setEditedModel({
                            ...editedModel,
                            abilities: updatedAbilities as string[] | { name: string; description: string }[],
                          });
                        }}
                        placeholder="Description..."
                        className="w-full bg-black/30 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none text-sm"
                        rows={2}
                      />
                    </div>
                  ))}
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newAbility}
                      onChange={(e) => setNewAbility(e.target.value)}
                      placeholder="Ability name..."
                      className="w-full bg-black/40 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none"
                    />
                    <textarea
                      value={newAbilityDescription}
                      onChange={(e) => setNewAbilityDescription(e.target.value)}
                      placeholder="Description..."
                      className="w-full bg-black/40 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none"
                      rows={2}
                    />
                    <button
                      onClick={addAbility}
                      className="w-full bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
                    >
                      Add Ability
                    </button>
                  </div>
                </div>
              </div>

              {editedModel.type === "elite" && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-bold text-white uppercase tracking-wide">
                      Skills
                    </label>
                  </div>
                  <div className="space-y-2">
                    {editedModel.skills.map((skill, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-black/40 px-3 py-2 rounded"
                      >
                        <div className="flex-1">
                          <span className="font-semibold text-white">{skill.name}</span>
                          <span className="text-ledger-muted text-sm ml-2">
                            ({skill.table})
                          </span>
                          {skill.description && (
                    <p className="text-white text-sm mt-1">
                              {skill.description}
                    </p>
                  )}
                        </div>
                        <button
                          onClick={() =>
                            setEditedModel({
                              ...editedModel,
                              skills: editedModel.skills.filter(
                                (_, i) => i !== idx
                              ),
                            })
                          }
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={16} />
                        </button>
                </div>
              ))}
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={newSkill.name}
                        onChange={(e) =>
                          setNewSkill({ ...newSkill, name: e.target.value })
                        }
                        placeholder="Skill name..."
                        className="bg-black/40 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none"
                      />
                      <input
                        type="text"
                        value={newSkill.table}
                        onChange={(e) =>
                          setNewSkill({ ...newSkill, table: e.target.value })
                        }
                        placeholder="Table (e.g., Melee)"
                        className="bg-black/40 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none"
                      />
                    </div>
                    <input
                      type="text"
                      value={newSkill.description}
                      onChange={(e) =>
                        setNewSkill({
                          ...newSkill,
                          description: e.target.value,
                        })
                      }
                      placeholder="Description..."
                      className="w-full bg-black/40 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none"
                    />
                    <button
                      onClick={addSkill}
                      className="w-full bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
                    >
                      Add Skill
                    </button>
            </div>
          </div>
              )}

          <div>
                <label className="block text-sm font-bold text-white mb-1 uppercase tracking-wide">
              Scars
            </label>
            <div className="space-y-2">
              {editedModel.scars.map((scar, idx) => (
                <div
                  key={idx}
                      className="flex items-center gap-2 bg-black/40 px-3 py-2 rounded"
                >
                  <span className="flex-1 text-white">{scar}</span>
                  <button
                    onClick={() => removeScar(idx)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newScar}
                  onChange={(e) => setNewScar(e.target.value)}
                  placeholder="Add new scar..."
                      className="flex-1 bg-black/40 text-white px-3 py-2 rounded border border-black/50 focus:border-ledger-gold focus:outline-none"
                />
                <button
                  onClick={addScar}
                  className="bg-red-900 text-white px-4 py-2 rounded hover:bg-red-800"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
            </>
          )}


          {/* Footer */}
          <div className="border-t border-gray-700 mt-6 pt-4">
            <div className={isEditMode ? "grid grid-cols-2 gap-2" : "flex gap-2"}>
              {isEditMode ? (
                <>
                  <button
                    onClick={handleSave}
                    className="bg-red-900 hover:bg-red-800 text-white px-3 py-2 rounded font-bold border border-red-800 uppercase tracking-wider text-xs flex items-center justify-center gap-1"
                  >
                    <Save size={14} />
                    SAVE
                  </button>
                  <button
                    onClick={() => {
                      setEditedModel({ ...model });
                      setIsEditMode(false);
                    }}
                    className="bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded font-semibold border border-gray-700 uppercase tracking-wider text-xs"
                  >
                    CANCEL
                  </button>
                  <button
                    onClick={() => {
                      console.log('REFUND MODEL clicked');
                      handleRefundModel();
                    }}
                    className="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded font-semibold border border-green-400 uppercase tracking-wider text-xs flex items-center justify-center gap-1"
                  >
                    🔄 REFUND MODEL
                  </button>
                  <button
                    onClick={onDelete}
                    className="bg-red-900 hover:bg-red-800 text-white px-3 py-2 rounded font-semibold border border-red-800 uppercase tracking-wider text-xs flex items-center gap-1"
                  >
                    <Trash2 size={14} />
                    DELETE
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      const updatedModel = {
                        ...editedModel,
                        reserve: !editedModel.reserve
                      };
                      setEditedModel(updatedModel);
                      onSave(updatedModel);
                    }}
                    className={`px-3 py-2 rounded font-semibold border uppercase tracking-wider text-xs ${
                      editedModel.reserve
                        ? "bg-yellow-700 hover:bg-yellow-600 text-yellow-100 border-yellow-600"
                        : "bg-blue-700 hover:bg-blue-600 text-blue-100 border-blue-600"
                    }`}
                  >
                    {editedModel.reserve ? "TAKE OUT OF RESERVE" : "RESERVE UNIT"}
                  </button>
                  {embedded && (
                    <>
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-2 border border-[#6b2a2a] uppercase tracking-wider text-xs font-semibold flex items-center justify-center gap-1"
                      >
                        <Edit2 size={14} />
                        EDIT STATS
                      </button>
                      <button
                        onClick={openImagePopup}
                        className="bg-[#2a1010] hover:bg-[#341414] text-gray-100 px-3 py-2 border border-[#6b2a2a] uppercase tracking-wider text-xs font-semibold flex items-center justify-center gap-1"
                      >
                        BG IMAGE
                      </button>
                    </>
                  )}
                  <button
                    onClick={onDelete}
                    className="bg-red-900 hover:bg-red-800 text-white px-3 py-2 rounded font-semibold border border-red-800 uppercase tracking-wider text-xs flex items-center justify-center gap-1"
                  >
                    <Trash2 size={14} />
                    DELETE
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showItemEditor && (
        <ItemEditor
          item={null}
          onSave={addItem}
          onCancel={() => {
            setShowItemEditor(false);
            setItemFilterCategory(undefined);
          }}
          filterCategory={itemFilterCategory}
          allItemTemplates={itemTemplates}
          updateItemTemplates={updateItemTemplates}
        />
      )}

      {showImagePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="w-full max-w-md rounded border border-white/10 bg-slate-950 p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold uppercase tracking-wider text-white">Edit Background Image</div>
              <button
                onClick={() => setShowImagePopup(false)}
                className="text-gray-300 hover:text-white"
              >
                ✕
              </button>
            </div>
            <label className="block text-xs uppercase tracking-wider text-gray-300 mb-2">Image URL</label>
            <input
              type="text"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              placeholder="https://..."
              className="w-full rounded border border-gray-700 bg-black/80 px-3 py-2 text-white outline-none focus:border-blue-500"
            />
            <div className="mt-4 text-xs uppercase tracking-wider text-gray-400 mb-3">General Background Images</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pb-1">
              {backgroundOptions.map((option) => {
                const selected = option.url === imageUrlInput.trim();
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => selectBackgroundOption(option.url)}
                    className={`group relative overflow-hidden rounded border transition ${selected ? "border-blue-500 ring-2 ring-blue-500" : "border-gray-700 hover:border-gray-500"}`}
                  >
                    <img
                      src={option.thumbUrl}
                      alt={option.key}
                      loading="lazy"
                      decoding="async"
                      width="128"
                      height="80"
                      className="h-20 w-full object-cover"
                    />
                    {selected && (
                      <div className="pointer-events-none absolute inset-0 border-2 border-blue-400" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={saveImagePopup}
                className="flex-1 bg-blue-700 hover:bg-blue-600 text-white px-3 py-2 rounded uppercase tracking-wider text-xs font-semibold"
              >
                Save
              </button>
              <button
                onClick={() => setShowImagePopup(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-200 px-3 py-2 rounded uppercase tracking-wider text-xs font-semibold"
              >
                Cancel
              </button>
            </div>
            <div className="mt-3 text-xs text-gray-400">
              Leave blank and save to clear the custom model background.
            </div>
          </div>
        </div>
      )}
      {showSkillModal && (
        <SkillModal
          onSave={handleSkillSelected}
          onClose={() => setShowSkillModal(false)}
        />
      )}
    </div>
  );
};
